import React, { useEffect, useState } from "react"
import { useLazyQuery, useQuery } from "@apollo/client"
import ALL_STORE_GROUPS from "graphql/queries/store/AllStoreGroups"
import PageHeader from "components/Ui/Page/PageHeader"
import { useDispatch, useSelector } from "react-redux"
import { selectUserGroups } from "lib/store/services/auth/selectors"
import { UserError } from "../UserError"
import SingleSelect from "../../components/Ui/Form/SingleSelect"
import { Option, Query } from "lib/types/common"
import { Flex, Layout, TimeZoneWrapper } from "./DashboardsSales.styled"
import TopProductsList from "../../components/DashboardSales/TopProductsList"
import TopDiscountCodesList from "../../components/DashboardSales/TopDiscountCodesList"
import { AnalyticsOrdersOverview } from "../../components/DashboardSales/AnalyticsOrdersOverview"
import { OrdersOverTimeChart } from "../../components/DashboardSales/OrdersOverTimeChart"
import {
  selectDiscountCodeRuleNames,
  selectDiscountCodeRuleNamesLoaded,
  selectEndDate,
  selectFilteredByExcludingTax,
  selectProductNames,
  selectSelectedCampaignGroups,
  selectSelectedCampaigns,
  selectStartDate,
  selectStoreGroups,
  selectStoreMarkets,
  selectTopCodesFilteredByHits,
  selectTopDiscountFilteredByType,
  selectTopProductsFilteredBy,
  selectTopProductsFilteredByHits,
  selectTopProductsFilteredByType,
  selectTopProductsSortBy
} from "lib/store/services/analytics/selectors"
import esb from "elastic-builder"
import dayjs from "dayjs"
import SEARCH_ORDERS_TOTAL_HITS from "../../graphql/queries/order/SearchOrdersTotalHits"
import {
  filterByAll,
  filterByDiscount,
  filterByNonDiscount,
  filterByParent,
  filterByVariant
} from "../../components/DashboardSales/AnalyticsData"
import {
  DiscountCodeRuleSearchHits,
  OrderSearchHits,
  ProductParentSearchHits
} from "lib/types/generated/graphql-types"
import { DashboardBarChart } from "../../components/DashboardSales/DashboardBarChart"
import { IconWrapper } from "../../components/DashboardSales/OrderChart.styled"
import { ReactComponent as ClockIcon } from "../../images/icons/clock.svg"
import { ReactComponent as CardIcon } from "../../images/icons/credit-card.svg"
import PeriodSelections from "../../components/DashboardSales/PeriodSelections"
import { getPaymentProviderDisplayName } from "helpers/getPaymentDisplayName"
import {
  addData,
  bucket,
  groupByPaymentMethodName,
  groupDiscountCodesByUppercaseKey,
  nestedDiscountCodeRuleData,
  topDiscountData,
  topDiscountRulesData
} from "./DashboardSalesAggregations"
import SEARCH_PRODUCT_PARENTS from "../../graphql/queries/product/SearchProductParents"
import SEARCH_DISCOUNT_CODE_RULES from "../../graphql/queries/discount/SearchDiscountCodeRules"
import {
  setDiscountCodeRuleNames,
  setProductNames,
  setTopProductsSortBy
} from "lib/store/services/analytics/slice"
import { useCurrencyRates } from "../../components/DashboardSales/hooks/useCurrencyRates"

export interface dataObj {
  name: string
  value: number
}

export function DashboardsSales() {
  const initialData: bucket[] = []
  const initialDiscountData: topDiscountData[] = []
  const [topDiscountCodes, setDiscountCodes] = useState(initialDiscountData)
  const [topDiscountCodeRules, setDiscountCodeRules] = useState(initialDiscountData)
  const [topProductVariantsAll, setTopProductVariantsAll] = useState(initialData)
  const [topProductVariantsCampaign, setTopProductVariantsCampaign] = useState(initialData)
  const [topProductVariantsNonDiscount, setTopProductVariantsNonDiscount] = useState(initialData)
  const [topProductParentsAll, setTopProductParentsAll] = useState(initialData)
  const [topProductParentsCampaign, setTopProductParentsCampaign] = useState(initialData)
  const [topProductParentsNonDiscount, setTopProductParentsNonDiscount] = useState(initialData)
  const topProductsFilteredBy = useSelector(selectTopProductsFilteredBy)
  const topProductsFilteredByType = useSelector(selectTopProductsFilteredByType)
  const topProductsSortBy = useSelector(selectTopProductsSortBy)
  const topProductsFilteredByHits = useSelector(selectTopProductsFilteredByHits)
  const topCodesFilteredByHits = useSelector(selectTopCodesFilteredByHits)
  const topDiscountFilteredByType = useSelector(selectTopDiscountFilteredByType)
  const [orders, setOrders] = useState<OrderSearchHits>()
  const [currency, setCurrency] = useState<Option>({
    value: "SEK",
    label: "SEK"
  })
  const [timeZone, setTimeZone] = useState<Option>({
    value: Intl.DateTimeFormat().resolvedOptions().timeZone,
    label: Intl.DateTimeFormat().resolvedOptions().timeZone
  })

  const availableCurrencies = ["SEK", "NOK", "DKK", "EUR", "USD"]
  const { data: storeGroupsData, loading: storeGroupsLoading } = useQuery(ALL_STORE_GROUPS, {
    fetchPolicy: "cache-first"
  })

  const [uniqueBaseCurrencies, setUniqueBaseCurrencies] = useState<string[]>([])
  const { ratesMap, loading: loadingRates } = useCurrencyRates(
    uniqueBaseCurrencies,
    availableCurrencies
  )

  const userGroups = useSelector(selectUserGroups)
  const timeZones = Intl.supportedValuesOf("timeZone")
  const startDate = useSelector(selectStartDate)
  const endDate = useSelector(selectEndDate)
  const selectedStoreGroups = useSelector(selectStoreGroups)
  const selectedStoreMarkets = useSelector(selectStoreMarkets)
  const selectedCampaignGroups = useSelector(selectSelectedCampaignGroups)
  const selectedCampaigns = useSelector(selectSelectedCampaigns)

  const [searchOrders, { loading: searchOrdersLoading, error }] = useLazyQuery(
    SEARCH_ORDERS_TOTAL_HITS,
    {
      onCompleted: (data) => {
        setOrders(data?.searchOrders)
      }
    }
  )

  useEffect(() => {
    const startDateCheck = new Date(startDate)
    const startDateIsValid = startDateCheck && !isNaN(startDateCheck.getTime())
    const endDateCheck = new Date(endDate)
    const endDateIsValid = endDateCheck && !isNaN(endDateCheck.getTime())
    if (!startDateIsValid || !endDateIsValid) return

    const boolQuery = esb
      .boolQuery()
      .must(
        esb
          .rangeQuery("date")
          .gt(dayjs(startDate).format("YYYY-MM-DD-HH:mm"))
          .lt(dayjs(endDate).format("YYYY-MM-DD-HH:mm"))
          .format("yyyy-MM-dd-HH:mm")
          .timeZone(timeZone.value)
      )

    if (selectedStoreGroups?.length > 0) {
      boolQuery.must(
        esb.termsQuery(
          "storeGroupId",
          selectedStoreGroups.map((option) => option.value)
        )
      )
    }
    if (selectedStoreMarkets.length > 0) {
      boolQuery.must(
        esb.termsQuery(
          "countryCode",
          selectedStoreMarkets.map((option) => option.value)
        )
      )
    }

    // Add hierarchical campaign filtering
    if (selectedCampaigns.length > 0) {
      // If specific campaigns are selected, filter by campaign IDs
      boolQuery.must(
        esb
          .nestedQuery()
          .path("orderLines")
          .query(
            esb.termsQuery(
              "orderLines.campaign.campaignId",
              selectedCampaigns.map((campaign) => campaign.value)
            )
          )
      )
    } else if (selectedCampaignGroups.length > 0) {
      // If only campaign groups are selected, filter by campaign group IDs
      boolQuery.must(
        esb
          .nestedQuery()
          .path("orderLines")
          .query(
            esb.termsQuery(
              "orderLines.campaign.campaignGroupId",
              selectedCampaignGroups.map((group) => group.value)
            )
          )
      )
    }

    const ordersTopValues = esb
      .reverseNestedAggregation("ordersTopValues")
      .aggs([
        esb.termsAggregation("topCountries", "countryCode").size(1).order("_count", "desc"),
        esb.termsAggregation("topStores", "storeGroupId").size(1).order("_count", "desc")
      ])

    const topProductDetails = esb
      .topHitsAggregation("topProductDetails")
      .size(1)
      .source([
        "orderLines.productParentId",
        "orderLines.productVariantId",
        "orderLines.imageUrl",
        "orderLines.quantity",
        "orderLines.name",
        "orderLines.campaign.name"
      ])

    const excludingTaxOrderLine = `
      def totalPriceAmount = 0;
      if (doc.containsKey('orderLines.distributedTotalPriceAmount') && !doc['orderLines.distributedTotalPriceAmount'].empty) {
        totalPriceAmount = doc['orderLines.distributedTotalPriceAmount'].value;
      }

      def totalTaxAmount = 0;
      if (doc.containsKey('orderLines.distributedTotalTaxAmount') && !doc['orderLines.distributedTotalTaxAmount'].empty) {
        totalTaxAmount = doc['orderLines.distributedTotalTaxAmount'].value;
      }

      def isTaxIncluded = true;
      if (doc.containsKey('isTaxIncludedInPrice') && !doc['isTaxIncludedInPrice'].empty) {
        isTaxIncluded = doc['isTaxIncludedInPrice'].value;
      }

      return isTaxIncluded ? totalPriceAmount - totalTaxAmount : totalPriceAmount;
    `

    const includingTaxOrderLine = `
      def totalPriceAmount = 0;
      if (doc.containsKey('orderLines.distributedTotalPriceAmount') && !doc['orderLines.distributedTotalPriceAmount'].empty) {
        totalPriceAmount = doc['orderLines.distributedTotalPriceAmount'].value;
      }

      def totalTaxAmount = 0;
      if (doc.containsKey('orderLines.distributedTotalTaxAmount') && !doc['orderLines.distributedTotalTaxAmount'].empty) {
        totalTaxAmount = doc['orderLines.distributedTotalTaxAmount'].value;
      }

      def isTaxIncluded = true;
      if (doc.containsKey('isTaxIncludedInPrice') && !doc['isTaxIncludedInPrice'].empty) {
        isTaxIncluded = doc['isTaxIncludedInPrice'].value;
      }

      return isTaxIncluded ? totalPriceAmount : totalPriceAmount + totalTaxAmount;
    `

    const totalValuesPerCurrencyCode = esb
      .termsAggregation("totalValuesPerCurrencyCode", "orderLines.currencyCode")
      .aggs([
        esb
          .sumAggregation("totalExcludingTax")
          .script(esb.script().lang("painless").inline(excludingTaxOrderLine)),
        esb
          .sumAggregation("totalIncludingTax")
          .script(esb.script().lang("painless").inline(includingTaxOrderLine))
      ])

    const dailyRevenueQuery = esb.requestBodySearch().query(boolQuery)
    const query = dailyRevenueQuery.toJSON() as Query

    const sortField =
      topProductsSortBy === "totalSales" && selectedStoreMarkets.length === 1
        ? "priceTotal"
        : "totalQuantity"

    const excludingTax = `
      def grandTotal = 0;
      if (doc.containsKey('totals.grandTotal') && !doc['totals.grandTotal'].empty) {
        grandTotal = doc['totals.grandTotal'].value;
      }

      def taxTotal = 0;
      if (doc.containsKey('totals.taxTotal') && !doc['totals.taxTotal'].empty) {
        taxTotal = doc['totals.taxTotal'].value;
      }

      def isTaxIncluded = true;
      if (doc.containsKey('isTaxIncludedInPrice') && !doc['isTaxIncludedInPrice'].empty) {
        isTaxIncluded = doc['isTaxIncludedInPrice'].value;
      }

      return isTaxIncluded ? grandTotal - taxTotal : grandTotal;
    `

    const includingTax = `
      def grandTotal = 0;
      if (doc.containsKey('totals.grandTotal') && !doc['totals.grandTotal'].empty) {
        grandTotal = doc['totals.grandTotal'].value;
      }

      def taxTotal = 0;
      if (doc.containsKey('totals.taxTotal') && !doc['totals.taxTotal'].empty) {
        taxTotal = doc['totals.taxTotal'].value;
      }

      def isTaxIncluded = true;
      if (doc.containsKey('isTaxIncludedInPrice') && !doc['isTaxIncludedInPrice'].empty) {
        isTaxIncluded = doc['isTaxIncludedInPrice'].value;
      }

      return isTaxIncluded ? grandTotal : grandTotal + taxTotal;
    `

    const aggs = esb
      .requestBodySearch()
      .agg(
        esb
          .termsAggregation("totalValuesPerCurrencyCode", "currencyCode")
          .aggs([
            esb
              .sumAggregation("totalExcludingTax")
              .script(esb.script().lang("painless").inline(excludingTax)),
            esb
              .sumAggregation("totalIncludingTax")
              .script(esb.script().lang("painless").inline(includingTax))
          ])
      )
      .agg(
        esb
          .termsAggregation("totalValuesPerStoreGroup", "storeGroupId")
          .aggs([
            esb
              .sumAggregation("totalExcludingTax")
              .script(esb.script().lang("painless").inline(excludingTax)),
            esb
              .sumAggregation("totalIncludingTax")
              .script(esb.script().lang("painless").inline(includingTax))
          ])
      )
      .agg(
        esb
          .termsAggregation("topDiscountCodes", "discountCodes")
          .size(50)
          .order("_count", "desc")
          .aggs([
            esb.termsAggregation("byCountry", "countryCode").order("_count", "desc"),
            esb.termsAggregation("byStoreGroupId", "storeGroupId").order("_count", "desc"),
            esb.sumAggregation("subTotal", "totals.subTotal"),
            esb.sumAggregation("discountTotal", "totals.discountTotal")
          ])
      )
      .agg(
        esb.nestedAggregation("topDiscountCodeRules", "orderLines").aggs([
          esb
            .termsAggregation("ruleIds", "orderLines.discountOutcome.codeRules.ruleId")
            .size(50)
            .order("order_level>unique_orders", "desc")
            .aggs([
              esb
                .reverseNestedAggregation("order_level")
                .aggs([
                  esb.cardinalityAggregation("unique_orders", "id"),
                  esb.termsAggregation("byCountry", "countryCode").order("_count", "desc"),
                  esb.termsAggregation("byStoreGroupId", "storeGroupId").order("_count", "desc"),
                  esb.sumAggregation("subTotal", "totals.subTotal"),
                  esb.sumAggregation("discountTotal", "totals.discountTotal")
                ])
            ])
        ])
      )
      .agg(
        esb.nestedAggregation("topProductParents", "orderLines").aggs([
          esb
            .termsAggregation("topList", "orderLines.productParentId")
            .size(50)
            .order(sortField, "desc")
            .aggs([
              esb.sumAggregation("totalQuantity", "orderLines.quantity"),
              esb.sumAggregation("priceTotal", "orderLines.distributedTotalPriceAmount"),
              esb.sumAggregation("discountTotal", "orderLines.distributedTotalDiscountAmount"),
              ordersTopValues,
              topProductDetails,
              totalValuesPerCurrencyCode
            ])
        ])
      )
      .agg(
        esb.nestedAggregation("topProductParentsFilterByNonDiscount", "orderLines").aggs([
          esb
            .filterAggregation(
              "filtered_orderLines",
              esb.termsQuery("orderLines.distributedTotalDiscountAmount", 0)
            )
            .aggs([
              esb
                .termsAggregation("topList", "orderLines.productParentId")
                .size(50)
                .order(sortField, "desc")
                .aggs([
                  esb.sumAggregation("totalQuantity", "orderLines.quantity"),
                  esb.sumAggregation("priceTotal", "orderLines.distributedTotalPriceAmount"),
                  esb.sumAggregation("discountTotal", "orderLines.distributedTotalDiscountAmount"),
                  ordersTopValues,
                  topProductDetails,
                  totalValuesPerCurrencyCode
                ])
            ])
        ])
      )
      .agg(
        esb.nestedAggregation("topProductParentsFilterByCampaign", "orderLines").aggs([
          esb
            .filterAggregation(
              "filtered_orderLines",
              esb.rangeQuery("orderLines.distributedTotalDiscountAmount").gt(0)
            )
            .aggs([
              esb
                .termsAggregation("topList", "orderLines.productParentId")
                .size(50)
                .order(sortField, "desc")
                .aggs([
                  esb.sumAggregation("totalQuantity", "orderLines.quantity"),
                  esb.sumAggregation("priceTotal", "orderLines.distributedTotalPriceAmount"),
                  esb.sumAggregation("discountTotal", "orderLines.distributedTotalDiscountAmount"),
                  ordersTopValues,
                  topProductDetails,
                  totalValuesPerCurrencyCode
                ])
            ])
        ])
      )
      .agg(
        esb.nestedAggregation("topProductVariantsFilterByNonDiscount", "orderLines").aggs([
          esb
            .filterAggregation(
              "filtered_orderLines",
              esb.termsQuery("orderLines.distributedTotalDiscountAmount", 0)
            )
            .aggs([
              esb
                .termsAggregation("topList", "orderLines.productVariantId")
                .size(50)
                .order(sortField, "desc")
                .aggs([
                  esb.sumAggregation("totalQuantity", "orderLines.quantity"),
                  esb.sumAggregation("priceTotal", "orderLines.distributedTotalPriceAmount"),
                  esb.sumAggregation("discountTotal", "orderLines.distributedTotalDiscountAmount"),
                  ordersTopValues,
                  topProductDetails,
                  totalValuesPerCurrencyCode
                ])
            ])
        ])
      )
      .agg(
        esb.nestedAggregation("topProductVariantsFilterByCampaign", "orderLines").aggs([
          esb
            .filterAggregation(
              "filtered_orderLines",
              esb.rangeQuery("orderLines.distributedTotalDiscountAmount").gt(0)
            )
            .aggs([
              esb
                .termsAggregation("topList", "orderLines.productVariantId")
                .size(50)
                .order(sortField, "desc")
                .aggs([
                  esb.sumAggregation("totalQuantity", "orderLines.quantity"),
                  esb.sumAggregation("priceTotal", "orderLines.distributedTotalPriceAmount"),
                  esb.sumAggregation("discountTotal", "orderLines.distributedTotalDiscountAmount"),
                  ordersTopValues,
                  topProductDetails,
                  totalValuesPerCurrencyCode
                ])
            ])
        ])
      )
      .agg(
        esb.nestedAggregation("topProductVariants", "orderLines").aggs([
          esb
            .termsAggregation("topList", "orderLines.productVariantId")
            .size(50)
            .order(sortField, "desc")
            .aggs([
              esb.sumAggregation("totalQuantity", "orderLines.quantity"),
              esb.sumAggregation("priceTotal", "orderLines.distributedTotalPriceAmount"),
              esb.sumAggregation("discountTotal", "orderLines.distributedTotalDiscountAmount"),
              ordersTopValues,
              topProductDetails,
              totalValuesPerCurrencyCode
            ])
        ])
      )
      .agg(esb.termsAggregation("totalByPaymentMethod", "paymentMethod"))
      .agg(esb.termsAggregation("totalByPaymentProvider", "paymentProviderName"))
      .toJSON() as Query

    const delayDebounceFn = setTimeout(() => {
      if (query) {
        searchOrders({
          variables: {
            from: 0,
            size: 0,
            query: JSON.stringify({
              bool: {
                must: [query.query]
              }
            }),
            trackTotalHits: true,
            aggs: JSON.stringify(aggs.aggs)
          }
        }).then((response) => {
          setOrders(response?.data?.searchOrders)
        })
      }
    })
    return () => clearTimeout(delayDebounceFn)
  }, [
    startDate,
    endDate,
    selectedStoreGroups,
    selectedStoreMarkets,
    selectedCampaignGroups,
    selectedCampaigns,
    timeZone,
    topProductsSortBy
  ])

  const initData: dataObj[] = []
  const [salesByCurrency, setSalesByCurrency] = useState(initData)
  const [countByCurrency, setCountByCurrency] = useState(initData)
  const [countByStoreGroup, setCountByStoreGroup] = useState(initData)
  const [countByPaymentMethod, setCountByPaymentMethod] = useState(initData)
  const [countByPaymentProvider, setCountByPaymentProvider] = useState(initData)
  const discountCodeRuleNames = useSelector(selectDiscountCodeRuleNames)
  const discountCodeRuleNamesLoaded = useSelector(selectDiscountCodeRuleNamesLoaded)
  const filteredByExcludingTax = useSelector(selectFilteredByExcludingTax)
  const filterByType = useSelector(selectTopProductsFilteredByType)
  const dispatch = useDispatch()
  const productNames = useSelector(selectProductNames)

  // Auto-switch to quantity sorting when switching from 1 market to multiple or no markets
  useEffect(() => {
    if (selectedStoreMarkets.length !== 1 && topProductsSortBy === "totalSales") {
      dispatch(setTopProductsSortBy("quantity"))
    }
  }, [selectedStoreMarkets.length, topProductsSortBy])

  const [searchProducts, { loading: loadingProducts }] = useLazyQuery(SEARCH_PRODUCT_PARENTS, {
    onCompleted: (data) => {
      const resp = data?.searchProductParents as ProductParentSearchHits
      if (resp.hits) {
        dispatch(setProductNames(Object.fromEntries(resp.hits.map((x) => [x.id, x.name]))))
      }
    }
  })

  const [searchDiscountRules] = useLazyQuery(SEARCH_DISCOUNT_CODE_RULES, {
    onCompleted: (data) => {
      const resp = data?.searchDiscountCodeRules as DiscountCodeRuleSearchHits
      if (resp.hits) {
        dispatch(
          setDiscountCodeRuleNames(
            Object.fromEntries(resp.hits.map((rule) => [rule.id, rule.name]))
          )
        )
      }
    }
  })

  function getDiscountCodesStoreName(codesData: topDiscountData) {
    const newCodesData = { ...codesData }
    newCodesData.byStoreGroupId.buckets = codesData.byStoreGroupId.buckets.map((y) => {
      const storeGroup = storeGroupsData?.getStoreGroups.find((s: { id: string }) => s.id === y.key)
      return {
        ...y,
        key: storeGroup?.name ?? y.key
      }
    })
    return newCodesData
  }

  function getProductStoreName(data: bucket) {
    const newCodesData = { ...data }
    newCodesData.ordersTopValues.topStores.buckets = data.ordersTopValues.topStores.buckets.map(
      (y) => {
        const storeGroup = storeGroupsData?.getStoreGroups.find(
          (s: { id: string }) => s.id === y.key
        )
        return {
          ...y,
          key: storeGroup?.name ?? y.key
        }
      }
    )
    return newCodesData
  }

  useEffect(() => {
    const aggregatedData = orders?.aggregations
      ? (JSON.parse(orders?.aggregations) as addData)
      : ({
          totalValuesPerCurrencyCode: { buckets: [] },
          totalValuesPerStoreGroup: { buckets: [] },
          totalByPaymentMethod: { buckets: [] },
          totalByPaymentProvider: { buckets: [] },
          topProductVariants: { topList: { buckets: [] } },
          topProductVariantsFilterByCampaign: { filtered_orderLines: { topList: { buckets: [] } } },
          topProductVariantsFilterByNonDiscount: {
            filtered_orderLines: { topList: { buckets: [] } }
          },
          topProductParents: { topList: { buckets: [] } },
          topProductParentsFilterByCampaign: { filtered_orderLines: { topList: { buckets: [] } } },
          topProductParentsFilterByNonDiscount: {
            filtered_orderLines: { topList: { buckets: [] } }
          },
          topDiscountCodes: { buckets: [] },
          topDiscountCodeRules: { ruleIds: { buckets: [] } }
        } as addData)

    let filterParents = aggregatedData?.topProductParents.topList.buckets.slice(
      0,
      topProductsFilteredByHits
    )
    filterParents = filterParents?.map((x) => getProductStoreName(x))
    setTopProductParentsAll(filterParents)

    let filterParentsCampaign =
      aggregatedData?.topProductParentsFilterByCampaign.filtered_orderLines.topList.buckets.slice(
        0,
        topProductsFilteredByHits
      )
    filterParentsCampaign = filterParentsCampaign?.map((x) => getProductStoreName(x))
    setTopProductParentsCampaign(filterParentsCampaign)

    let filterParentsNonDiscount =
      aggregatedData?.topProductParentsFilterByNonDiscount.filtered_orderLines.topList.buckets.slice(
        0,
        topProductsFilteredByHits
      )
    filterParentsNonDiscount = filterParentsNonDiscount?.map((x) => getProductStoreName(x))
    setTopProductParentsNonDiscount(filterParentsNonDiscount)

    let filterVariants = aggregatedData?.topProductVariants.topList.buckets.slice(
      0,
      topProductsFilteredByHits
    )
    filterVariants = filterVariants?.map((x) => getProductStoreName(x))
    setTopProductVariantsAll(filterVariants)

    let filterDiscountCodes = aggregatedData?.topDiscountCodes?.buckets.slice(
      0,
      topCodesFilteredByHits
    )
    filterDiscountCodes = filterDiscountCodes?.map((codesData) => {
      return getDiscountCodesStoreName(codesData)
    })
    setDiscountCodes(groupDiscountCodesByUppercaseKey(filterDiscountCodes))

    // Process discount rules data from nested aggregation
    let filterDiscountRules = aggregatedData?.topDiscountCodeRules?.ruleIds?.buckets.slice(
      0,
      topCodesFilteredByHits
    )

    // Transform the nested structure to match our expected format
    const transformedRules =
      (filterDiscountRules as nestedDiscountCodeRuleData[])?.map((ruleData) => ({
        doc_count:
          ruleData.order_level?.unique_orders?.value ||
          ruleData.order_level?.doc_count ||
          ruleData.doc_count,
        key: ruleData.key,
        subTotal: ruleData.order_level?.subTotal || { value: 0 },
        discountTotal: ruleData.order_level?.discountTotal || { value: 0 },
        byCountry: ruleData.order_level?.byCountry || { buckets: [] },
        byStoreGroupId: ruleData.order_level?.byStoreGroupId || { buckets: [] }
      })) || []

    const rulesWithStoreNames = transformedRules?.map((rulesData) => {
      return getDiscountCodesStoreName(rulesData)
    })

    // Add rule names to the rules data
    const rulesWithNames: topDiscountRulesData[] =
      rulesWithStoreNames?.map((rule) => ({
        ...rule,
        name: discountCodeRuleNames[rule.key] || rule.key
      })) || []

    setDiscountCodeRules(rulesWithNames)

    let filterVariantsCampaign =
      aggregatedData?.topProductVariantsFilterByCampaign.filtered_orderLines.topList.buckets.slice(
        0,
        topProductsFilteredByHits
      )
    filterVariantsCampaign = filterVariantsCampaign?.map((x) => getProductStoreName(x))
    setTopProductVariantsCampaign(filterVariantsCampaign)

    let filterVariantsNonDiscount =
      aggregatedData?.topProductVariantsFilterByNonDiscount.filtered_orderLines.topList.buckets.slice(
        0,
        topProductsFilteredByHits
      )
    filterVariantsNonDiscount = filterVariantsNonDiscount?.map((x) => getProductStoreName(x))
    setTopProductVariantsNonDiscount(filterVariantsNonDiscount)

    const byCurrency = aggregatedData?.totalValuesPerCurrencyCode.buckets.map((x) => {
      return {
        name: x.key,
        value: filteredByExcludingTax ? x.totalExcludingTax.value : x.totalIncludingTax.value
      }
    })
    setSalesByCurrency(byCurrency)

    setUniqueBaseCurrencies([...new Set(byCurrency.map((item) => item.name))])

    setCountByCurrency(
      aggregatedData?.totalValuesPerCurrencyCode.buckets.map((x) => {
        return {
          name: x.key,
          value: x.doc_count
        }
      })
    )

    setCountByStoreGroup(
      aggregatedData?.totalValuesPerStoreGroup.buckets.map((x) => {
        return {
          name:
            storeGroupsData?.getStoreGroups.find((s: { id: string }) => s.id === x.key)?.name ??
            x.key,
          value: x.doc_count
        }
      })
    )

    setCountByPaymentMethod(groupByPaymentMethodName(aggregatedData?.totalByPaymentMethod.buckets))

    setCountByPaymentProvider(
      aggregatedData?.totalByPaymentProvider.buckets.map((x) => {
        return {
          name: getPaymentProviderDisplayName(x.key),
          value: x.doc_count
        }
      })
    )

    const boolQuery = esb.boolQuery().must(
      esb.termsQuery(
        "id",
        filterParents.map((p) => p.topProductDetails?.hits.hits[0]._source.productParentId ?? "")
      )
    )
    const productsQuery = esb.requestBodySearch().query(boolQuery)
    const query = productsQuery.toJSON() as Query

    const delayDebounceFn = setTimeout(() => {
      if (query) {
        searchProducts({
          variables: {
            from: 0,
            size: 50,
            query: JSON.stringify(query?.query),
            trackTotalHits: true
          }
        })
      }
    })
    return () => clearTimeout(delayDebounceFn)
  }, [
    orders,
    topProductsFilteredByHits,
    topCodesFilteredByHits,
    filterByType,
    filteredByExcludingTax,
    discountCodeRuleNames
  ])

  // Fetch discount rule names when switching to rules view (only if not already loaded)
  useEffect(() => {
    if (topDiscountFilteredByType === "code rules" && !discountCodeRuleNamesLoaded) {
      searchDiscountRules({
        variables: {
          from: 0,
          size: 400,
          sort: [{ field: "created", order: "DESC" }]
        }
      })
    }
  }, [topDiscountFilteredByType, discountCodeRuleNamesLoaded])

  const loading = storeGroupsLoading || searchOrdersLoading

  if (!userGroups) return <UserError />

  return (
    <>
      <PageHeader title="Sales Dashboard" />
      <TimeZoneWrapper>
        <Flex>
          <IconWrapper>
            <ClockIcon />
          </IconWrapper>
        </Flex>
        <SingleSelect
          options={timeZones.map((timeZone) => ({ value: timeZone, label: timeZone }))}
          setValue={setTimeZone}
          defaultValue={{
            value: Intl.DateTimeFormat().resolvedOptions().timeZone,
            label: Intl.DateTimeFormat().resolvedOptions().timeZone
          }}
        />
        <Flex>
          <IconWrapper>
            <CardIcon />
          </IconWrapper>
        </Flex>
        <SingleSelect
          options={availableCurrencies.map((currency) => ({ value: currency, label: currency }))}
          setValue={setCurrency}
          defaultValue={{
            value: "SEK",
            label: "SEK"
          }}
        />
      </TimeZoneWrapper>
      <PeriodSelections
        timeZone={timeZone.value}
        storeGroups={storeGroupsData?.getStoreGroups}
        loading={loading}
      ></PeriodSelections>
      <Layout className={loading ? "blur" : ""}>
        <AnalyticsOrdersOverview
          timeZone={timeZone.value}
          currencyCode={currency.value}
          totalOrders={orders?.total ?? 0}
          data={salesByCurrency}
          filterCurrencies={availableCurrencies}
          ratesMap={ratesMap}
          loadingRates={loadingRates}
        />
      </Layout>
      <Layout className={loading ? "blur" : ""}>
        <OrdersOverTimeChart
          startDate={startDate}
          endDate={endDate}
          timeZone={timeZone.value}
          storeGroups={selectedStoreGroups}
          storeMarkets={selectedStoreMarkets}
          campaignGroups={selectedCampaignGroups}
          campaigns={selectedCampaigns}
        />
      </Layout>
      <Layout className={loading ? "blur" : ""}>
        <DashboardBarChart
          title={"Sales By Store"}
          data={countByStoreGroup}
          totalCount={orders?.total ?? 0}
        ></DashboardBarChart>
        <DashboardBarChart
          title={"Sales By Currency"}
          data={countByCurrency}
          totalCount={orders?.total ?? 0}
        ></DashboardBarChart>
        <DashboardBarChart
          title={"Sales By Payments"}
          data={countByPaymentMethod}
          totalCount={orders?.total ?? 0}
        ></DashboardBarChart>
        <DashboardBarChart
          title={"Sales By PSP"}
          data={countByPaymentProvider}
          totalCount={orders?.total ?? 0}
        ></DashboardBarChart>
      </Layout>
      <Layout className={loading ? "blur" : ""}>
        <TopDiscountCodesList
          data={topDiscountFilteredByType === "codes" ? topDiscountCodes : topDiscountCodeRules}
          loading={loading}
          error={error}
          type={topDiscountFilteredByType}
        ></TopDiscountCodesList>
      </Layout>
      {topProductsFilteredBy === filterByAll && topProductsFilteredByType === filterByVariant ? (
        <TopProductsList
          data={topProductVariantsAll}
          loading={loading}
          error={error}
          currencyCode={currency.value}
          ratesMap={ratesMap}
        ></TopProductsList>
      ) : topProductsFilteredBy === filterByAll && topProductsFilteredByType === filterByParent ? (
        <TopProductsList
          productParentNames={productNames}
          data={topProductParentsAll}
          loading={loading && loadingProducts}
          error={error}
          currencyCode={currency.value}
          ratesMap={ratesMap}
        ></TopProductsList>
      ) : topProductsFilteredBy === filterByDiscount &&
        topProductsFilteredByType === filterByVariant ? (
        <TopProductsList
          data={topProductVariantsCampaign}
          loading={loading}
          error={error}
          currencyCode={currency.value}
          ratesMap={ratesMap}
        ></TopProductsList>
      ) : topProductsFilteredBy === filterByDiscount &&
        topProductsFilteredByType === filterByParent ? (
        <TopProductsList
          productParentNames={productNames}
          data={topProductParentsCampaign}
          loading={loading && loadingProducts}
          error={error}
          currencyCode={currency.value}
          ratesMap={ratesMap}
        ></TopProductsList>
      ) : topProductsFilteredBy === filterByNonDiscount &&
        topProductsFilteredByType === filterByVariant ? (
        <TopProductsList
          data={topProductVariantsNonDiscount}
          loading={loading}
          error={error}
          currencyCode={currency.value}
          ratesMap={ratesMap}
        ></TopProductsList>
      ) : topProductsFilteredBy === filterByNonDiscount &&
        topProductsFilteredByType === filterByParent ? (
        <TopProductsList
          productParentNames={productNames}
          data={topProductParentsNonDiscount}
          loading={loading && loadingProducts}
          error={error}
          currencyCode={currency.value}
          ratesMap={ratesMap}
        ></TopProductsList>
      ) : (
        <></>
      )}
    </>
  )
}
